import { useNavigate, useParams } from 'react-router-dom';

import AdminAssignmentsService from '../../api/AdminAssignmentsService';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import { Cadence } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useUpdateWorkflowResultGroup = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { setToast } = useToastStore();
  const { watch } = useFormContext<AssignmentFormData>();
  const { currentInstanceParentWorkflowId } = useAdminAssignmentsStore();

  const { settings } = watch();

  const {
    daysToComplete,
    dueFromHired,
    escalateBuddy,
    escalateManager,
    escalationContacts,
    nonReminderMessages,
    overdueMessages,
    reminderMessages,
  } = settings;

  const payload = {
    id,
    resultGroupUpdateInput: {
      daysToComplete: daysToComplete ?? 0,
      dueFromHired,
      escalateBuddy,
      escalateManager,
      escalationContacts: escalationContacts.map((contact) => contact.id),
      nonReminderMessages: nonReminderMessages.map((message) => message.id),
      overdueMessages: overdueMessages.map((message) => ({
        cadence: message.cadence as Cadence,
        message: message.message.id,
        totalMessages: message.totalMessages,
      })),
      reminderMessages: reminderMessages.map((reminder) => ({
        message: reminder.message.id,
        timeBeforeDue: reminder.timeBeforeDue,
      })),
    },
  };

  const updateWorkflowResultGroupMutation = useMutation({
    mutationFn: () => AdminAssignmentsService.updateWorkflowResultGroup(payload),
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.instances.update.success'),
      });

      navigate(`/admin/assignments/details/${currentInstanceParentWorkflowId}/instance/${id}`, {
        replace: true,
      });
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.instances.update.error'),
      }),
  });

  return { updateWorkflowResultGroupMutation };
};
